import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryGuideForm from '../components/DeliveryGuideForm';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  getPurchaseById,
  getDeliverySummaryForPurchase,
  getDeliveryGuideById,
  updateDeliveryGuide,
  deleteDeliveryGuide,
} from '../api';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import { buildDeliveryGuideLineKeys } from '../utils/deliveryGuideItems';
import useConfirmDialog from '../hooks/useConfirmDialog';

const DeliveryGuideEditPage = () => {
  const { branchId, purchaseId, guideId } = useParams();
  const navigate = useNavigate();

  const [purchase, setPurchase] = useState(null);
  const [summary, setSummary] = useState([]);
  const [guide, setGuide] = useState(null);
  const [itemsToDeliver, setItemsToDeliver] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [driverName, setDriverName] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');

  const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveTimeoutRef = useRef(null);
  const autoSaveInitializedRef = useRef(false);
  const lastSavedSignatureRef = useRef('');
  const lastAttemptedSignatureRef = useRef('');
  const { user } = useCurrentUser();
  const canUpdateGuide = hasPermission(user, 'delivery_guide.update');
  const canDeleteGuide = hasPermission(user, 'delivery_guide.delete');
  const { confirm } = useConfirmDialog();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purchaseData, summaryData, guideData] = await Promise.all([
          getPurchaseById(resolvedBranchId, purchaseId),
          getDeliverySummaryForPurchase(resolvedBranchId, purchaseId),
          getDeliveryGuideById(resolvedBranchId, guideId),
        ]);

        setPurchase(purchaseData);
        setSummary(summaryData.summary || []);
        setGuide(guideData);

        setDriverName(guideData.driverName || '');
        setVehiclePlate(guideData.vehiclePlate || '');
        if (guideData.deliveryDate) {
          const date = new Date(guideData.deliveryDate);
          const iso = date.toISOString().split('T')[0];
          setDeliveryDate(iso);
        }
        setNotes(guideData.notes || '');

        const initialItems = {};
        (guideData.items || []).forEach((item) => {
          const keys = buildDeliveryGuideLineKeys(item);
          keys.forEach((key) => {
            initialItems[key] = item.deliveredQuantity || '';
          });
        });
        setItemsToDeliver(initialItems);
      } catch (e) {
        console.error('Error loading delivery guide for edit', e);
        setError('Error al cargar la guía de despacho.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [branchId, purchaseId, guideId, resolvedBranchId]);

  const payload = useMemo(() => {
    const items = summary
      .map((line) => {
        const keys = buildDeliveryGuideLineKeys(line);
        const qty = keys.reduce((acc, lineKey) => {
          if (acc !== undefined && acc !== null) return acc;
          return itemsToDeliver[lineKey];
        }, undefined);
        if (!qty || qty <= 0) return null;
        return {
          varietyId: line.varietyId,
          varietyPriceId: line.varietyPriceId ?? null,
          priceIndex: Number.isInteger(line.priceIndex) ? line.priceIndex : null,
          metricUnit: line.metricUnit,
          varietyName: line.varietyName,
          deliveredQuantity: qty,
        };
      })
      .filter(Boolean);

    return {
      items,
      driverName: driverName || null,
      vehiclePlate: vehiclePlate || null,
      deliveryDate: deliveryDate || null,
      notes: notes || null,
    };
  }, [summary, itemsToDeliver, driverName, vehiclePlate, deliveryDate, notes]);

  const payloadSignature = useMemo(
    () => JSON.stringify(payload),
    [payload]
  );

  const handleQuantityChange = (key, remainingQuantityPlusThisGuide, value) => {
    if (!canUpdateGuide) return;
    setError('');
    const parsed = parseFloat(value.replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) {
      setItemsToDeliver((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    const maxForThisGuide = remainingQuantityPlusThisGuide;
    if (parsed > maxForThisGuide) {
      setItemsToDeliver((prev) => ({ ...prev, [key]: maxForThisGuide }));
      return;
    }
    setItemsToDeliver((prev) => ({ ...prev, [key]: parsed }));
  };

  const handleSetMax = (key, remainingQuantityPlusThisGuide) => {
    if (!canUpdateGuide) return;
    setError('');
    setItemsToDeliver((prev) => ({ ...prev, [key]: remainingQuantityPlusThisGuide }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canUpdateGuide) return;
    if (!purchase || !guide) return;
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (payload.items.length === 0) {
      setError('Debe seleccionar al menos un producto con cantidad a despachar.');
      return;
    }

    try {
      setSaving(true);
      await updateDeliveryGuide(resolvedBranchId, guide.id, payload);
      lastSavedSignatureRef.current = payloadSignature;
      navigate(`/branches/${resolvedBranchId}/purchases/${purchase.id}`);
    } catch (e) {
      console.error('Error updating delivery guide', e);
      const message = e.response?.data?.message || 'Error al actualizar la guía de despacho.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAutoSave = useCallback(async () => {
    if (!canUpdateGuide) return;
    if (!guide || !purchase || payload.items.length === 0) return;

    setAutoSaveStatus('saving');
    lastAttemptedSignatureRef.current = payloadSignature;
    try {
      await updateDeliveryGuide(resolvedBranchId, guide.id, payload);
      lastSavedSignatureRef.current = payloadSignature;
      setError('');
      setAutoSaveStatus('saved');
    } catch (e) {
      console.error('Error auto-saving delivery guide', e);
      setAutoSaveStatus('error');
    }
  }, [guide, payload, payloadSignature, purchase, resolvedBranchId, canUpdateGuide]);

  useEffect(() => {
    if (loading || saving || !guide || !purchase || autoSaveStatus === 'saving' || !canUpdateGuide) return;

    if (!autoSaveInitializedRef.current) {
      autoSaveInitializedRef.current = true;
      lastSavedSignatureRef.current = payloadSignature;
    }

    if (payload.items.length === 0) return;

    if (payloadSignature === lastSavedSignatureRef.current) return;
    if (autoSaveStatus === 'error' && payloadSignature === lastAttemptedSignatureRef.current) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 800);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [payloadSignature, payload, autoSaveStatus, loading, saving, guide, purchase, handleAutoSave]);

  useEffect(() => {
    if (autoSaveStatus !== 'saved') return;
    const timeout = setTimeout(() => setAutoSaveStatus('idle'), 2000);
    return () => clearTimeout(timeout);
  }, [autoSaveStatus]);

  const handleDelete = async () => {
    if (!canDeleteGuide) return;
    if (!guide) return;
    const approved = await confirm({
      title: 'Eliminar guía',
      message: '¿Eliminar esta guía de despacho?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    try {
      await deleteDeliveryGuide(resolvedBranchId, guide.id);
      navigate(`/branches/${resolvedBranchId}/purchases/${purchaseId}`);
    } catch (e) {
      console.error('Error deleting delivery guide', e);
      const message = e.response?.data?.message || 'Error al eliminar la guía de despacho.';
      setError(message);
    }
  };

  if (loading) {
    return (
      <p className="p-4 text-center">
        <InlineLoading label="Cargando guía de despacho" />
      </p>
    );
  }

  if (!purchase || !guide) {
    return <p className="p-4 text-center text-red-600">No se encontró la guía de despacho.</p>;
  }

  const originalGuideMap = {};
  (guide.items || []).forEach((item) => {
    const keys = buildDeliveryGuideLineKeys(item);
    keys.forEach((key) => {
      originalGuideMap[key] = item.deliveredQuantity || 0;
    });
  });

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-4 text-center">Editar Guía de Despacho</h1>

        {error && <p className="mb-4 text-center text-red-600">{error}</p>}
        {autoSaveStatus === 'saving' && (
          <p className="mb-4 text-center text-gray-600 text-sm">Guardando automáticamente...</p>
        )}
        {autoSaveStatus === 'saved' && (
          <p className="mb-4 text-center text-green-600 text-sm">Cambios guardados automáticamente.</p>
        )}
        {autoSaveStatus === 'error' && (
          <p className="mb-4 text-center text-red-600 text-sm">
            No se pudieron guardar los cambios automáticamente. Verifica tu conexión.
          </p>
        )}

        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <h2 className="text-lg font-semibold mb-2">Guía #{guide.id} — Compra #{purchase.id}</h2>
          {purchase.Client && (
            <div className="text-sm text-gray-700">
              <p className="font-semibold">{purchase.Client.name}</p>
              {purchase.Client.rut && <p>RUT: {purchase.Client.rut}</p>}
              {(purchase.Client.address || purchase.Client.city) && (
                <p>
                  {purchase.Client.address}
                  {purchase.Client.city && `, ${purchase.Client.city}`}
                </p>
              )}
            </div>
          )}
        </div>

        <DeliveryGuideForm
          onSubmit={handleSubmit}
          summary={summary}
          itemsToDeliver={itemsToDeliver}
          canEdit={canUpdateGuide}
          driverName={driverName}
          onDriverNameChange={(e) => setDriverName(e.target.value)}
          vehiclePlate={vehiclePlate}
          onVehiclePlateChange={(e) => setVehiclePlate(e.target.value)}
          deliveryDate={deliveryDate}
          onDeliveryDateChange={(e) => setDeliveryDate(e.target.value)}
          notes={notes}
          onNotesChange={(e) => setNotes(e.target.value)}
          itemsTitle="Productos en esta guía"
          emptyMessage="No hay productos pendientes para esta compra."
          labels={{
            vehiclePlate: 'Patente del vehículo',
            currentGuide: 'En esta guía',
            setMaxAria: 'Usar máximo disponible para este producto',
          }}
          getMaxForLine={(line) => {
            const remaining = line.remainingQuantity || 0;
            const originalQty = buildDeliveryGuideLineKeys(line).reduce((acc, lineKey) => {
              if (acc !== 0) return acc;
              return originalGuideMap[lineKey] || 0;
            }, 0);
            return remaining + originalQty;
          }}
          getRemainingForLine={(line) => line.remainingQuantity || 0}
          onQuantityChange={handleQuantityChange}
          onSetMax={handleSetMax}
          footerLeft={
            <button
              type="button"
              onClick={() =>
                navigate(`/branches/${resolvedBranchId}/purchases/${purchase.id}`)
              }
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Volver
            </button>
          }
          footerRight={
            <div className="flex gap-2">
              {canDeleteGuide && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Eliminar guía
                </button>
              )}
              {canUpdateGuide && (
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {saving ? <InlineLoading label="Guardando" /> : 'Guardar cambios'}
                </button>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default DeliveryGuideEditPage;
